import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { config } from '../config';
import { validateEmail, validatePassword, validateConfirmPassword } from '../utils/validation';
import poweredByStrava from '../assets/power_by_strava.png';
import ScreenshotCarousel from '../components/ScreenshotCarousel';
import HeartRateZones from '../components/HeartRateZones';

function SignUp() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [hasPremium, setHasPremium] = useState(false)
  const [heartRateZones, setHeartRateZones] = useState(null)
  const [zonesShowErrors, setZonesShowErrors] = useState(false)
  const [signUpError, setSignUpError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    // Check if user is already logged in
    const sessionData = sessionStorage.getItem('sessionData');
    if (sessionData) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSignUp = async (email, password) => {
    setEmailTouched(true)
    setPasswordTouched(true)
    setConfirmPasswordTouched(true)
    setSignUpError(null)

    if (validateEmail(email) || validatePassword(password) || validateConfirmPassword(password, confirmPassword)) {
      return
    }

    if (!hasPremium) {
      const zonesValid = heartRateZones && [1, 2, 3, 4, 5].every(n => {
        const z = heartRateZones[`zone${n}`];
        return z && z.from !== '' && z.to !== '' && Number(z.from) > 0 && Number(z.to) > 0;
      });
      if (!zonesValid) {
        setZonesShowErrors(true);
        return;
      }
    }

    let heartRateZonesBackend = null;
    if (heartRateZones) {
      heartRateZonesBackend = {};
      for (const index of [1, 2, 3, 4, 5]) {
        heartRateZonesBackend[`zone_${index}`] = {
          min: Number(heartRateZones[`zone${index}`].from),
          max: Number(heartRateZones[`zone${index}`].to),
        };
      }
    }

    try {
      setIsSubmitting(true)
      const response = await axios.post(
        `${config.getBackendUrl()}/initiate_signup`,
        new URLSearchParams({
          username: email,
          password: password,
          heart_rate_zones: heartRateZonesBackend ? JSON.stringify(heartRateZonesBackend) : '',
        })
      );
      window.location.href = config.getStravaAuthUrl(response.data.signup_token);
    } catch (err) {
      setSignUpError(err.response?.data?.detail || 'Sign-up failed. Please try again.');
      setIsSubmitting(false)
    }
  };
  

  return (
    <div className="center-container">
      <div className="card">
        <h1>Create Account</h1>
        <img src={poweredByStrava} alt="Powered by Strava" style={{ width: '180px', margin: '8px auto 16px' }} />
        <p style={{ marginBottom: '24px', color: '#666' }}>
          Set your credentials below. You'll then be redirected to Strava to authorize the app and complete your registration.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); handleSignUp(email, password); }}>
        <div className="field-wrapper">
          <label className="field-label" htmlFor="email">Email</label>
          <input
            id="email"
            className={`input${emailTouched && validateEmail(email) ? ' input-invalid' : emailTouched && !validateEmail(email) ? ' input-valid' : ''}`}
            type="email"
            placeholder=""
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
          />
          {emailTouched && validateEmail(email) && (
            <p className="field-error">{validateEmail(email)}</p>
          )}
        </div>
        <div className="field-wrapper">
          <label className="field-label" htmlFor="password">Password</label>
          <div className="password-wrapper">
            <input
              id="password"
              className={`input${passwordTouched && validatePassword(password) ? ' input-invalid' : passwordTouched && !validatePassword(password) ? ' input-valid' : ''}`}
              type={showPassword ? 'text' : 'password'}
              placeholder=""
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setPasswordTouched(true)}
            />
            <button type="button" className="password-toggle" onClick={() => setShowPassword(v => !v)}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {passwordTouched && validatePassword(password) && (
            <p className="field-error">{validatePassword(password)}</p>
          )}
        </div>
        <div className="field-wrapper">
          <label className="field-label" htmlFor="confirmPassword">Confirm Password</label>
          <div className="password-wrapper">
            <input
              id="confirmPassword"
              className={`input${confirmPasswordTouched && validateConfirmPassword(password, confirmPassword) ? ' input-invalid' : confirmPasswordTouched && !validateConfirmPassword(password, confirmPassword) ? ' input-valid' : ''}`}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder=""
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setConfirmPasswordTouched(true)}
            />
            <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(v => !v)}>
              {showConfirmPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {confirmPasswordTouched && validateConfirmPassword(password, confirmPassword) && (
            <p className="field-error">{validateConfirmPassword(password, confirmPassword)}</p>
          )}
        </div>
        <div className="field-wrapper">
          <label style={{ fontSize: 13, color: '#444', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hasPremium}
              onChange={(e) => setHasPremium(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            I have Strava Premium (heart rate zones will be imported automatically)
          </label>
        </div>

        <HeartRateZones hasPremium={hasPremium} onChange={setHeartRateZones} showErrors={zonesShowErrors} />

        {signUpError && <p className="field-error" style={{ marginBottom: '12px' }}>{signUpError}</p>}
        <button type="submit" className="button" disabled={isSubmitting}>
          {isSubmitting ? 'Redirecting to Strava...' : 'Authenticate with Strava'}
        </button>
        </form>
        <p style={{ marginTop: '16px' }}>
          Already have an account? <Link to="/">Login</Link>
        </p>
      </div>
      <ScreenshotCarousel />
    </div>
  );
}

export default SignUp;
